import {
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { useTheme } from '@parkdna/ui/theme';
import React, { useEffect, useMemo } from 'react';
import Joyride, {
  ACTIONS,
  CallBackProps,
  EVENTS,
  STATUS,
  Step,
} from 'react-joyride';
import { useLocation, useNavigate } from 'react-router-dom';

import { Subject, useAuth } from '@/hooks/auth/useAuth';
import { getEnvName } from '@/legacy/data/apiHelpers';
import { useToursStore } from '@/stores/tours.ts';

interface ValidatorTourProps {
  run: boolean;
  onFinish: () => void;
}

const ValidatorTour: React.FC<ValidatorTourProps> = ({ run, onFinish }) => {
  const { hasPermission, isLoading } = useAuth();
  const [theme] = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    stepIndex,
    tourActive,
    pendingNavigation,
    setStepIndex,
    setTotalSteps,
    setTourActive,
    setPendingNavigation,
  } = useToursStore();

  useEffect(() => {
    if (pendingNavigation && location.pathname === pendingNavigation) {
      setPendingNavigation(null);
      setTourActive(true);
    }
  }, [
    location.pathname,
    pendingNavigation,
    setPendingNavigation,
    setTourActive,
  ]);

  // Generate tour steps based on user permissions
  const tourSteps = useMemo(() => {
    const steps: Array<Step & { data?: { next?: string; previous?: string } }> =
      [
        {
          target: 'body',
          content: (
            <Box>
              <Typography variant="h3" gutterBottom>
                Welcome to ParkDNA Validator Portal! 🎉
              </Typography>
              <Typography variant="body1" paragraph>
                Let&apos;s take a quick tour of the key features available to
                you as a validator. This tour will guide you through the main
                sections you&apos;ll be working with.
              </Typography>
              <Typography variant="body2" paragraph>
                We&apos;ll start by exploring your sublocations where you can
                manage validation settings and billing.
              </Typography>
              <Chip
                label="Interactive Tour"
                color="primary"
                variant="outlined"
                size="small"
              />
            </Box>
          ),
          placement: 'center' as const,
          data: {
            next: '/validations/sublocations',
          },
        },
      ];

    // Add Sublocations step if user has access - only show if we're on the sublocations page
    if (hasPermission(Subject.VALIDATION_CONFIGS, 'read')) {
      steps.push({
        target:
          location.pathname === '/validations/sublocations'
            ? '[data-testid="sidebar-menu-item-sublocations"]'
            : 'body',
        content: (
          <Box>
            <Typography variant="h4" gutterBottom>
              📍 Sublocations
            </Typography>
            {location.pathname === '/validations/sublocations' ? (
              <>
                <Typography variant="body2" paragraph>
                  Perfect! You&apos;re now on the sublocations page. Here you
                  can:
                </Typography>
                <List dense>
                  <ListItem disablePadding>
                    <ListItemText>
                      <Typography variant="body2">
                        View all sublocations and their usage
                      </Typography>
                    </ListItemText>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText>
                      <Typography variant="body2">
                        Create new sublocations
                      </Typography>
                    </ListItemText>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText>
                      <Typography variant="body2">
                        Configure billing and payment methods
                      </Typography>
                    </ListItemText>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText>
                      <Typography variant="body2">
                        Set validation duration and access codes
                      </Typography>
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body2" paragraph sx={{ mt: 2 }}>
                  Next, let&apos;s explore the validations list to see
                  transaction history.
                </Typography>
              </>
            ) : (
              <Typography variant="body2" paragraph>
                Click &quot;Next&quot; to navigate to your sublocations page
                where you can manage validation settings.
              </Typography>
            )}
          </Box>
        ),
        disableBeacon: true,
        placement:
          location.pathname === '/validations/sublocations'
            ? ('right' as const)
            : ('center' as const),
        data: {
          next: '/validations/sublocations',
          prev: '/validations/sublocations',
        },
      });

      // Add detailed sublocations page tour steps - always include them
      steps.push({
        target:
          location.pathname === '/validations/sublocations'
            ? '[data-tourid="sublocations-data-grid"]'
            : 'body',
        content: (
          <Box>
            <Typography variant="h5" gutterBottom>
              📊 Sublocations Data Grid
            </Typography>
            {location.pathname === '/validations/sublocations' ? (
              <>
                <Typography variant="body2" paragraph>
                  This data grid shows all your validation sublocations with key
                  information:
                </Typography>
              </>
            ) : (
              <Typography variant="body2" paragraph>
                When you&apos;re on the sublocations page, you&apos;ll see a
                comprehensive data grid showing all your validation sublocations
                with their status, usage, and payment information.
              </Typography>
            )}
            {location.pathname === '/validations/sublocations' && (
              <>
                <List dense>
                  <ListItem disablePadding>
                    <ListItemText>
                      <Typography variant="body2">
                        <strong>Location & Sublocation:</strong> Where
                        validations are available
                      </Typography>
                    </ListItemText>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText>
                      <Typography variant="body2">
                        <strong>Status:</strong> Active/Inactive validation
                        zones
                      </Typography>
                    </ListItemText>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText>
                      <Typography variant="body2">
                        <strong>Usage:</strong> Current validation counts and
                        limits
                      </Typography>
                    </ListItemText>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText>
                      <Typography variant="body2">
                        <strong>Payment Status:</strong> Billing and payment
                        method status
                      </Typography>
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body2" paragraph sx={{ mt: 1 }}>
                  You can sort, filter, and search through your sublocations
                  here.
                </Typography>
              </>
            )}
          </Box>
        ),
        placement: 'top',
        disableBeacon: true,
        data: {
          next: '/validations/list',
          prev: '/validations/sublocations',
        },
        spotlightClicks: true,
      });
    }

    // Add Validations step if user has access
    if (hasPermission(Subject.VALIDATION_CONFIGS, 'read')) {
      steps.push({
        target:
          location.pathname === '/validations/list'
            ? '[data-testid="sidebar-menu-item-validations"]'
            : 'body',
        content: (
          <Box>
            <Typography variant="h4" gutterBottom>
              ✅ Validations
            </Typography>
            {location.pathname === '/validations/list' ? (
              <>
                <Typography variant="body2" paragraph>
                  Excellent! Now you&apos;re viewing the validations list. Here
                  you can:
                </Typography>
                <List dense>
                  <ListItem disablePadding>
                    <ListItemText>
                      <Typography variant="body2">
                        See all validation transactions
                      </Typography>
                    </ListItemText>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText>
                      <Typography variant="body2">
                        Filter by location and date range
                      </Typography>
                    </ListItemText>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText>
                      <Typography variant="body2">
                        Track validation usage and patterns
                      </Typography>
                    </ListItemText>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText>
                      <Typography variant="body2">
                        Monitor validation performance
                      </Typography>
                    </ListItemText>
                  </ListItem>
                </List>
              </>
            ) : (
              <Typography variant="body2" paragraph>
                Click &quot;Next&quot; to navigate to the validations list where
                you can view all validation transactions.
              </Typography>
            )}
          </Box>
        ),
        disableBeacon: true,
        placement:
          location.pathname === '/validations/list'
            ? ('right' as const)
            : ('center' as const),
        data: {
          next: '/validations/list',
          prev: '/validations/sublocations',
        },
      });

      // Add detailed validations page tour steps - always include them
      steps.push(
        {
          target:
            location.pathname === '/validations/list'
              ? '[data-tourid="validations-location-filter"]'
              : 'body',
          content: (
            <Box>
              <Typography variant="h5" gutterBottom>
                📍 Location Filter
              </Typography>
              {location.pathname === '/validations/list' ? (
                <>
                  <Typography variant="body2" paragraph>
                    Use this location selector to filter validations by specific
                    locations.
                  </Typography>
                  <Typography variant="body2" paragraph>
                    You can:
                  </Typography>
                  <List dense>
                    <ListItem disablePadding>
                      <ListItemText>
                        <Typography variant="body2">
                          Select multiple locations to compare data
                        </Typography>
                      </ListItemText>
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemText>
                        <Typography variant="body2">
                          View all locations or narrow down to specific ones
                        </Typography>
                      </ListItemText>
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemText>
                        <Typography variant="body2">
                          Quickly switch between different validation zones
                        </Typography>
                      </ListItemText>
                    </ListItem>
                  </List>
                </>
              ) : (
                <Typography variant="body2" paragraph>
                  The validations page includes a location filter dropdown where
                  you can select specific locations to analyze validation data
                  and compare performance across different zones.
                </Typography>
              )}
            </Box>
          ),
          placement:
            location.pathname === '/validations/list'
              ? ('bottom' as const)
              : ('center' as const),
          disableBeacon: true,
          data: {
            next: '/validations/list',
            prev: '/validations/list',
          },
        },
        {
          target: '[data-tourid="validations-status-filters"]',
          content: (
            <Box>
              <Typography variant="h5" gutterBottom>
                🔍 Status Filtering
              </Typography>
              {location.pathname === '/validations/list' ? (
                <>
                  <Typography variant="body2" paragraph>
                    Filter validations by their current status using these chip
                    filters.
                  </Typography>
                  <Typography variant="body2" paragraph>
                    Available status filters:
                  </Typography>
                  <List dense>
                    <ListItem disablePadding>
                      <ListItemText>
                        <Typography variant="body2">
                          <strong>Active:</strong> Currently valid parking
                          validations
                        </Typography>
                      </ListItemText>
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemText>
                        <Typography variant="body2">
                          <strong>Expired:</strong> Validations that have ended
                        </Typography>
                      </ListItemText>
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemText>
                        <Typography variant="body2">
                          <strong>Nearing Expiration:</strong> About to expire
                        </Typography>
                      </ListItemText>
                    </ListItem>
                  </List>
                </>
              ) : (
                <Typography variant="body2" paragraph>
                  The validations page features status filter chips that allow
                  you to quickly filter validations by their current state -
                  Active, Expired, or Nearing Expiration.
                </Typography>
              )}
            </Box>
          ),
          placement:
            location.pathname === '/validations/list'
              ? ('bottom' as const)
              : ('center' as const),
          disableBeacon: true,
          data: {
            next: '/validations/list',
            prev: '/validations/list',
          },
        },
        {
          target: '[data-tourid="validations-date-filter"]',
          content: (
            <Box>
              <Typography variant="h5" gutterBottom>
                📅 Date Range Filter
              </Typography>
              {location.pathname === '/validations/list' ? (
                <>
                  <Typography variant="body2" paragraph>
                    Filter validations by creation date to analyze specific time
                    periods.
                  </Typography>
                  <Typography variant="body2" paragraph>
                    This helps you:
                  </Typography>
                  <List dense>
                    <ListItem disablePadding>
                      <ListItemText>
                        <Typography variant="body2">
                          Analyze validation patterns over time
                        </Typography>
                      </ListItemText>
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemText>
                        <Typography variant="body2">
                          Generate reports for specific periods
                        </Typography>
                      </ListItemText>
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemText>
                        <Typography variant="body2">
                          Track seasonal or event-based usage
                        </Typography>
                      </ListItemText>
                    </ListItem>
                  </List>
                </>
              ) : (
                <Typography variant="body2" paragraph>
                  The validations page includes a date range filter that helps
                  you analyze validation patterns over specific time periods and
                  generate targeted reports.
                </Typography>
              )}
            </Box>
          ),
          placement:
            location.pathname === '/validations/list'
              ? ('bottom' as const)
              : ('center' as const),
          disableBeacon: true,
          data: {
            next: '/validations/list',
            prev: '/validations/list',
          },
        },
        {
          target:
            location.pathname === '/validations/list'
              ? '[data-tourid="validations-data-grid"]'
              : 'body',
          content: (
            <Box>
              <Typography variant="h5" gutterBottom>
                📊 Validations Data Grid
              </Typography>
              <Typography variant="body2" paragraph>
                This comprehensive data grid shows all validation transactions
                with detailed information:
              </Typography>
              <List dense>
                <ListItem disablePadding>
                  <ListItemText>
                    <Typography variant="body2">
                      <strong>Created At:</strong> When the validation was
                      issued
                    </Typography>
                  </ListItemText>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemText>
                    <Typography variant="body2">
                      <strong>Duration:</strong> How long the validation is
                      valid for
                    </Typography>
                  </ListItemText>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemText>
                    <Typography variant="body2">
                      <strong>Plate & State:</strong> Vehicle information
                    </Typography>
                  </ListItemText>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemText>
                    <Typography variant="body2">
                      <strong>Status:</strong> Current validation status with
                      visual indicators
                    </Typography>
                  </ListItemText>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemText>
                    <Typography variant="body2">
                      <strong>Location Details:</strong> Exact sublocation
                      information
                    </Typography>
                  </ListItemText>
                </ListItem>
              </List>
              <Typography variant="body2" paragraph sx={{ mt: 1 }}>
                You can sort by any column and use the built-in search
                functionality.
              </Typography>
            </Box>
          ),
          placement: 'top',
          disableBeacon: true,
          data: {
            next: hasPermission(Subject.REPORTS, 'generate') ? '/reports' : '/',
            prev: '/validations/list',
          },
        }
      );
    }

    // Add Reports step if user has access
    if (hasPermission(Subject.REPORTS, 'generate')) {
      steps.push({
        target:
          location.pathname === '/reports'
            ? '[data-testid="sidebar-menu-item-reports"]'
            : 'body',
        content: (
          <Box>
            <Typography variant="h4" gutterBottom>
              📊 Reports
            </Typography>
            {location.pathname === '/reports' ? (
              <>
                <Typography variant="body2" paragraph>
                  Great! You&apos;re now on the reports page. As a validator,
                  you can:
                </Typography>
                <List dense>
                  <ListItem disablePadding>
                    <ListItemText>
                      <Typography variant="body2">
                        Generate validation summary reports with aggregated data
                      </Typography>
                    </ListItemText>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText>
                      <Typography variant="body2">
                        Create detailed validation reports with individual
                        records
                      </Typography>
                    </ListItemText>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText>
                      <Typography variant="body2">
                        Filter by date range and location
                      </Typography>
                    </ListItemText>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText>
                      <Typography variant="body2">
                        Export data for further analysis
                      </Typography>
                    </ListItemText>
                  </ListItem>
                </List>
              </>
            ) : (
              <Typography variant="body2" paragraph>
                Click &quot;Next&quot; to navigate to the reports section where
                you can generate detailed analytics.
              </Typography>
            )}
          </Box>
        ),
        disableBeacon: true,
        placement:
          location.pathname === '/reports'
            ? ('right' as const)
            : ('center' as const),
        data: {
          next: '/reports', // Stay on reports page
          prev: '/validations/list',
        },
      });

      // Add detailed reports page tour steps - always include them
      steps.push(
        {
          target:
            location.pathname === '/reports'
              ? '[data-tourid="reports-type-selector"]'
              : 'body',
          content: (
            <Box>
              <Typography variant="h5" gutterBottom>
                📋 Report Type Selection
              </Typography>
              {location.pathname === '/reports' ? (
                <>
                  <Typography variant="body2" paragraph>
                    As a validator, you have access to validation reports to
                    analyze your validation operations:
                  </Typography>
                  <List dense>
                    <ListItem disablePadding>
                      <ListItemText>
                        <Typography variant="body2">
                          <strong>Validation Summary:</strong> Aggregated
                          validation data grouped by day, week, or month
                        </Typography>
                      </ListItemText>
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemText>
                        <Typography variant="body2">
                          <strong>Validation Detail:</strong> Individual
                          validation records with complete transaction details
                        </Typography>
                      </ListItemText>
                    </ListItem>
                  </List>
                </>
              ) : (
                <Typography variant="body2" paragraph>
                  The reports page provides validation summary and detail
                  reports to help you analyze your validation operations and
                  track performance metrics.
                </Typography>
              )}
            </Box>
          ),
          placement: 'right' as const,
          disableBeacon: true,
          data: {
            next: '/reports',
            prev: '/reports',
          },
        },
        {
          target:
            location.pathname === '/reports'
              ? '[data-tourid="reports-date-range"]'
              : 'body',
          content: (
            <Box>
              <Typography variant="h5" gutterBottom>
                📅 Date Range Selection
              </Typography>
              {location.pathname === '/reports' ? (
                <>
                  <Typography variant="body2" paragraph>
                    Select the time period for your report analysis.
                  </Typography>
                  <Typography variant="body2" paragraph>
                    You can:
                  </Typography>
                  <List dense>
                    <ListItem disablePadding>
                      <ListItemText>
                        <Typography variant="body2">
                          Choose custom date ranges for specific analysis
                          periods
                        </Typography>
                      </ListItemText>
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemText>
                        <Typography variant="body2">
                          Use preset ranges like &quot;Last 7 days&quot; or
                          &quot;This month&quot;
                        </Typography>
                      </ListItemText>
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemText>
                        <Typography variant="body2">
                          Compare different time periods for trend analysis
                        </Typography>
                      </ListItemText>
                    </ListItem>
                  </List>
                </>
              ) : (
                <Typography variant="body2" paragraph>
                  The reports page includes a date range selector where you can
                  choose custom time periods or use preset ranges for your
                  analysis.
                </Typography>
              )}
            </Box>
          ),
          placement: 'right' as const,
          disableBeacon: true,
          data: {
            next: '/reports', // Stay on reports page
            prev: '/reports',
          },
        },
        {
          target:
            location.pathname === '/reports'
              ? '[data-tourid="reports-generate-button"]'
              : 'body',
          content: (
            <Box>
              <Typography variant="h5" gutterBottom>
                🚀 Generate Report
              </Typography>
              <Typography variant="body2" paragraph>
                Click this button to generate your customized report with the
                selected parameters.
              </Typography>
              <Typography variant="body2" paragraph>
                The system will:
              </Typography>
              <List dense>
                <ListItem disablePadding>
                  <ListItemText>
                    <Typography variant="body2">
                      Process your data in real-time
                    </Typography>
                  </ListItemText>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemText>
                    <Typography variant="body2">
                      Display results in an interactive data grid
                    </Typography>
                  </ListItemText>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemText>
                    <Typography variant="body2">
                      Provide export options for further analysis
                    </Typography>
                  </ListItemText>
                </ListItem>
              </List>
            </Box>
          ),
          placement:
            location.pathname === '/reports'
              ? ('right' as const)
              : ('center' as const),
          disableBeacon: true,
          data: {
            next: '/reports', // Stay on reports page
            prev: '/reports',
          },
        }
      );
    }

    // Add final step
    steps.push({
      target: 'body',
      content: (
        <Box>
          <Typography variant="h3" gutterBottom>
            🚀 You&apos;re all set!
          </Typography>
          <Typography variant="body1" paragraph>
            You now know the main areas of the ParkDNA Validator Portal. Feel
            free to explore each section and don&apos;t hesitate to reach out if
            you need help!
          </Typography>
          <Box mt={2} mb={1}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              💡 Pro tip:
            </Typography>
            <Typography variant="body2">
              You can restart this tour anytime by clicking the &quot;Take a
              tour&quot; button in the sidebar.
            </Typography>
          </Box>
          <Box mt={2}>
            <Chip label="Happy validating!" color="primary" variant="filled" />
          </Box>
        </Box>
      ),
      placement: 'center' as const,
      data: {
        previous: hasPermission(Subject.REPORTS, 'generate')
          ? '/reports'
          : '/validations/list',
      },
    });

    return steps;
  }, [hasPermission, location.pathname]);

  // Update total steps whenever tour steps change
  useEffect(() => {
    if (isLoading) return;

    setTotalSteps(tourSteps.length);
  }, [tourSteps.length, setTotalSteps, isLoading]);

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { action, index, status, type, step } = data;
    const { data: stepData } = step as {
      data?: { next?: string; prev?: string };
    };

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      onFinish();
      // set to the last step
      setStepIndex(tourSteps.length - 1);
    } else if (type === EVENTS.STEP_AFTER) {
      const isPreviousAction = action === ACTIONS.PREV;
      const nextRoute = isPreviousAction ? stepData?.prev : stepData?.next;

      if (nextRoute && nextRoute !== location.pathname) {
        // Stop the tour, navigate, and set up continuation
        setTourActive(false);
        setPendingNavigation(nextRoute);
        setStepIndex(index + (isPreviousAction ? -1 : 1));

        // Navigate after a short delay to allow tour to stop
        setTimeout(() => {
          navigate(nextRoute);
        }, 100);
      } else {
        // Update step index for same-route navigation
        setStepIndex(index + (isPreviousAction ? -1 : 1));
      }
    }
  };

  if (isLoading) return null;

  return (
    <Joyride
      run={run && tourActive}
      steps={tourSteps}
      stepIndex={stepIndex}
      continuous
      debug={getEnvName() === 'development'}
      showProgress
      showSkipButton
      callback={handleJoyrideCallback}
      styles={{
        options: {
          arrowColor: theme.palette.background.paper,
          backgroundColor: theme.palette.background.paper,
          overlayColor: 'rgba(0, 0, 0, 0.4)',
          primaryColor: theme.palette.primary.main,
          textColor: theme.palette.text.primary,
          width: 420,
          zIndex: theme.zIndex.modal + 1,
        },
        tooltip: {
          borderRadius: theme.shape.borderRadius,
          padding: '24px',
          boxShadow: theme.shadows[8],
        },
        tooltipContainer: {
          textAlign: 'left',
        },
        buttonNext: {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          borderRadius: theme.shape.borderRadius,
          padding: '10px 20px',
          fontSize: '0.875rem',
          fontWeight: 500,
        },
        buttonBack: {
          color: theme.palette.text.secondary,
          marginRight: '12px',
          fontSize: '0.875rem',
        },
        buttonSkip: {
          color: theme.palette.text.secondary,
          fontSize: '0.875rem',
        },
      }}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Finish Tour',
        next: 'Next',
        open: 'Open the dialog',
        skip: 'Skip Tour',
      }}
    />
  );
};

export default ValidatorTour;
